import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { staffProfileApi, ApiError } from '../api';
import type { StaffProfile, StaffDashboardData } from '../types/staff';

interface StaffProfileState {
	// Profile data
	profile: StaffProfile | null;
	// Dashboard data
	dashboard: StaffDashboardData | null;
	// Loading states
	isLoadingProfile: boolean;
	isLoadingDashboard: boolean;
	// Error states
	profileError: string | null;
	dashboardError: string | null;
	// Actions
	fetchProfile: () => Promise<void>;
	fetchDashboard: () => Promise<void>;
	clearProfile: () => void;
}

const useStaffProfileStore = create<StaffProfileState>()(
	devtools(
		set => ({
			// Initial state
			profile: null,
			dashboard: null,
			isLoadingProfile: false,
			isLoadingDashboard: false,
			profileError: null,
			dashboardError: null,

			// Fetch staff profile from API
			fetchProfile: async () => {
				set({ isLoadingProfile: true, profileError: null });

				try {
					const profile = await staffProfileApi.getProfile();

					set({
						profile,
						isLoadingProfile: false,
					});
				} catch (error) {
					const apiError = error as ApiError;
					set({
						profileError: apiError.message || 'Failed to load profile',
						isLoadingProfile: false,
					});

					if (import.meta.env.DEV) {
						// eslint-disable-next-line no-console
						console.error('Staff profile fetch error:', apiError);
					}
				}
			},

			// Fetch dashboard data
			fetchDashboard: async () => {
				set({ isLoadingDashboard: true, dashboardError: null });

				try {
					const dashboard = await staffProfileApi.getDashboard();

					set({
						dashboard,
						isLoadingDashboard: false,
					});
				} catch (error) {
					const apiError = error as ApiError;
					set({
						dashboardError: apiError.message || 'Failed to load dashboard',
						isLoadingDashboard: false,
					});

					if (import.meta.env.DEV) {
						// eslint-disable-next-line no-console
						console.error('Staff dashboard fetch error:', apiError);
					}
				}
			},

			// Clear all data (on logout)
			clearProfile: () => {
				set({
					profile: null,
					dashboard: null,
					isLoadingProfile: false,
					isLoadingDashboard: false,
					profileError: null,
					dashboardError: null,
				});
			},
		}),
		{ name: 'staff-profile-store' }
	)
);

export default useStaffProfileStore;
